import { defineStore } from 'pinia';
import { Draft, DraftType } from '../types/draft';
import { isSmartphone } from '../constants/ui';
import { showNotification } from '../vue/notifications/notification_actions';
import { instance } from '../api/instance';
import { ProjectApi } from '@/dashboard/api/project';
import { DraftsApi, UploadPart } from '@/dashboard/api/drafts';
import axios from 'axios';
import { getImageDimensions } from '@/dashboard/utils/draft/getImageDimensions';
import { getVideoDimensions } from '@/dashboard/utils/draft/getVideoDimensions';
import { blobToFile, getVideoThumbnailBlob } from '@/dashboard/utils/draft/getVideoThumbnailBlob';
import { BaseProject } from '@/dashboard/types/project';

type PublicStoreState = {
  project: BaseProject | undefined;
  projectShortLink: string | undefined;
  drafts: Draft[];
  page: number;
  mobileColumns: boolean;
  canModify: boolean;
  checkedDrafts: number[];
  deletedDrafts: number[];
  loading: boolean;
  uploading: boolean;
  allFilesCount: number;
  uploadedFilesCount: number;
  uploadingProgress: number;
  showUploadLimitBanner: boolean;
  showLimitPopup: boolean;
  bytesCanBeUsed: number;
  pageLoader: boolean;
};

export const usePublicStore = defineStore('publicStore', {
  state: (): PublicStoreState => {
    const mobileColumns = localStorage.getItem('mobileColumns');

    return {
      project: undefined,
      projectShortLink: undefined,
      drafts: [],
      deletedDrafts: [],
      checkedDrafts: [],
      loading: false,
      page: 1,
      uploading: false,
      allFilesCount: 0,
      uploadedFilesCount: 0,
      uploadingProgress: 0,
      mobileColumns: mobileColumns === null ? true : mobileColumns === 'true',
      canModify: true,
      showUploadLimitBanner: false,
      showLimitPopup: false,
      bytesCanBeUsed: 0,
      pageLoader: false,
    };
  },
  getters: {},
  actions: {
    setShortLink(shortLink: string) {
      this.projectShortLink = shortLink;
    },
    setProject(project: BaseProject) {
      this.project = project;
    },
    async loadDrafts(loaded: LoadedType) {
      const perPage: number = 35;
      const page: number = this.page;
      const drafts: Draft[] = this.drafts;
      const short_link = this.projectShortLink;

      if (!short_link) {
        return;
      }

      this.loading = true;

      const result = await DraftsApi.list({ page, short_link });

      if (this.canModify) {
        const loadedDrafts: Draft[] = result.drafts;

        this.drafts = [...drafts, ...loadedDrafts];
        this.page = this.page + 1;
        this.loading = false;

        loaded(loadedDrafts.length, perPage);
      }
    },
    async selectAll(short_link: string) {
      try {
        const { ids } = await ProjectApi.getUserDraftIds(short_link);
        this.checkedDrafts = ids;
      } catch (error) {
        console.log(error);
      }
    },
    async deleteDrafts(drafts: Draft[]): Promise<void> {
      this.deletedDrafts = [...this.deletedDrafts, ...drafts.map((item) => item.id)];
      const title = isSmartphone.value ? 'Item deleted' : 'The item has been permanently deleted';

      showNotification('delete_drafts', {
        data: drafts.map((item) => item.id),
        emoji: '❌',
        title: title,
        ok_action: isSmartphone.value ? undefined : (): void => {},
        undo_action: (): void => {
          this.undoDeletion(drafts.map((item) => item.id));
        },
      });
    },
    async deleteSelectedDrafts(): Promise<void> {
      const ids = [...this.checkedDrafts];
      const title = isSmartphone.value ? 'Items deleted' : 'The items has been permanently deleted';

      this.deletedDrafts = [...this.deletedDrafts, ...ids];

      showNotification('delete_drafts', {
        data: ids,
        emoji: '❌',
        title: title,
        ok_action: isSmartphone.value ? undefined : (): void => {},
        undo_action: (): void => {
          this.undoDeletion(ids);
        },
      });

      this.setCheckedDrafts([]);
    },
    async bulkMoveSelectedDrafts(projectId: number): Promise<void> {
      if (this.checkedDrafts.length <= 0) {
        return;
      }

      try {
        if (this.project?.id !== projectId) {
          const response = await DraftsApi.bulkMove({
            project_id: projectId,
            draft_ids: this.checkedDrafts,
          });

          if (response.success) {
            this.drafts = this.drafts.filter((draft: Draft) => !this.checkedDrafts.includes(draft.id));
          }
        }
      } finally {
        this.setCheckedDrafts([]);
      }
    },
    async moveDraft(draftId: number, projectId: number): Promise<void> {
      if (this.project?.id !== projectId) {
        const response = await DraftsApi.bulkMove({
          project_id: projectId,
          draft_ids: [draftId],
        });

        if (response.success) {
          this.drafts = this.drafts.filter((draft: Draft) => draft.id !== draftId);
          this.setCheckedDrafts(this.checkedDrafts.filter((id) => id !== draftId));
        }
      }
    },
    undoDeletion(ids: number[]): void {
      this.deletedDrafts = this.deletedDrafts.filter((obj: any) => !ids.includes(obj));
    },
    resetPage(): void {
      this.page = 1;
      this.drafts = [];
      this.deletedDrafts = [];
      this.projectShortLink = undefined;
    },

    async createDraft(files: FileList): Promise<void> {
      const short_link = this.projectShortLink;
      if (this.uploading || !short_link) return;

      this.uploading = true;
      this.uploadingProgress = 0;

      const fileArray = Array.from(files);
      const totalFiles = fileArray.length;
      const maxParallelUploads = 3;
      let uploadedFiles = 0;
      let activeUploads = 0;
      let lastDraftShortLink = '';
      let draftProjectName = '';

      // Function to simulate continuous uploading effect with slower progression
      const simulateProgress = () => {
        const interval = setInterval(() => {
          if (!this.uploading) {
            clearInterval(interval);
            return;
          }

          const progressLimit = Math.min(95, Math.floor((uploadedFiles / totalFiles) * 90) + 5);
          if (this.uploadingProgress < progressLimit) {
            this.uploadingProgress = Math.max(progressLimit, this.uploadingProgress + 1);
          }
        }, 500);
      };

      simulateProgress();

      // Function to process the uploads one by one
      const uploadFile = async (file: File) => {
        const formData = new FormData();
        formData.append('short_link', short_link);
        formData.append('file', file);

        try {
          const result = await instance.post(`/app/projects/upload`, formData);
          const upload_limit: boolean = result.data.upload_limit;
          lastDraftShortLink = result.data.short_link;
          draftProjectName = result.data.project_name;

          if (lastDraftShortLink === this.projectShortLink) {
            const newDraft: Draft = result.data.drafts[0];
            this.drafts = [newDraft, ...this.drafts]; // Insert immediately
            this.setShowUploadLimitBanner(upload_limit);
          }
        } catch (e: any) {
          console.error('Upload failed:', e);
        } finally {
          uploadedFiles++;
          activeUploads--;

          if (uploadedFiles === totalFiles) {
            if (lastDraftShortLink !== this.projectShortLink) {
              showNotification('any_type', {
                emoji: '👌',
                title: isSmartphone.value ? 'Uploading completed' : `Uploading in the ${draftProjectName} has finished`,
                ok_action: () => {},
              });
            }

            this.uploadingProgress = 100;
            setTimeout(() => (this.uploading = false), 1000);
          }
        }
      };

      // Process files with max 3 parallel requests
      const uploadNextBatch = async () => {
        while (uploadedFiles < totalFiles) {
          while (activeUploads < maxParallelUploads && uploadedFiles + activeUploads < totalFiles) {
            const file = fileArray[uploadedFiles + activeUploads];
            activeUploads++;
            uploadFile(file);
          }
          await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay to prevent race conditions
        }
      };

      await uploadNextBatch();

      // Wait for all uploads to complete
      const waitForUploads = async () => {
        while (activeUploads > 0) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      };
      await waitForUploads();

      // Ensure progress bar reaches 100%
      this.uploadingProgress = 100;
      setTimeout(() => (this.uploading = false), 1000);
    },

    async uploadDrafts(files: FileList): Promise<void> {
      const uploadChunk = async (signedUrl: string, chunk: Blob) => {
        const response = await axios.put(signedUrl, chunk, {
          headers: {
            'Content-Type': chunk.type || 'application/octet-stream',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Expose-Headers': 'Etag',
          },
        });
        return response.headers['etag'].replace(/"/g, '');
      };

      let hasError = false;
      const short_link = this.projectShortLink ?? '';
      const filesArray = Array.from(files);
      const totalFilesSize = filesArray.reduce((acc: number, file: File) => (acc += file.size), 0);
      let sizeUploaded = 0;
      let lastDraftShortLink = '';
      let draftProjectName = '';
      this.uploading = true;
      this.uploadingProgress = 0;
      this.uploadedFilesCount = 0;
      this.allFilesCount = filesArray.length;

      try {
        for (const file of filesArray) {
          const filename = file.name;
          let type = DraftType.file;
          let preview: Blob | undefined = undefined;
          let width: number | undefined = undefined;
          let height: number | undefined = undefined;

          if (file.type.startsWith('image')) {
            type = DraftType.image;
            const result = await getImageDimensions(file);
            width = result.width;
            height = result.height;
          } else if (file.type.startsWith('video') && file.type.includes('mp4')) {
            type = DraftType.video;
            const result = await getVideoDimensions(file);
            width = result.width;
            height = result.height;
            preview = await getVideoThumbnailBlob(file);
          }

          const chunkSize = 5 * 1024 * 1024; // 5MB chunks
          const totalChunks = Math.ceil(file.size / chunkSize);
          const response = await DraftsApi.initiateUpload({
            filename,
            short_link,
            file_size: file.size,
          });
          const uploadId = response.upload_id;
          const salt = response.salt;
          let draftUrl: string | null = null;

          let uploadedParts: UploadPart[] = [];

          for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const chunk = file.slice(start, end);
            const partNumber = i + 1;

            const response = await DraftsApi.getPreSignedUrl({
              filename,
              part_number: partNumber,
              upload_id: uploadId,
              salt,
            });
            const signedUrl = response.signed_url;
            draftUrl = signedUrl.split('?')[0];

            const etag = await uploadChunk(signedUrl, chunk);
            uploadedParts = [...uploadedParts, { part_number: partNumber, etag: etag }];

            sizeUploaded += chunk.size;
            this.uploadingProgress = Math.round((sizeUploaded / totalFilesSize) * 100);
          }

          await DraftsApi.completeUpload({ filename, upload_id: uploadId, salt, parts: uploadedParts });

          if (draftUrl && short_link) {
            const response = await DraftsApi.create({
              short_link,
              name: filename,
              draft_type: type,
              image_link: type === DraftType.image ? draftUrl : undefined,
              file_link: type !== DraftType.image ? draftUrl : undefined,
              preview: preview ? blobToFile(preview, 'thumbnail.jpg', 'image/jpeg') : undefined,
              width,
              height,
            });
            this.uploadedFilesCount += 1;

            if (response.success) {
              const upload_limit = response.upload_limit ?? false;
              const draft = response.draft;
              lastDraftShortLink = response.project_short_link ?? '';
              draftProjectName = response.project_name ?? '';

              if (lastDraftShortLink === this.projectShortLink && draft) {
                this.drafts = [draft, ...this.drafts];
                this.setShowUploadLimitBanner(upload_limit);
              }
            } else {
              break;
            }
          }
        }
      } catch {
        this.uploading = false;
        hasError = true;
      } finally {
        this.uploading = false;
        this.uploadingProgress = 100;

        if (lastDraftShortLink !== this.projectShortLink && !hasError) {
          showNotification('any_type', {
            emoji: '👌',
            title: isSmartphone.value ? 'Uploading completed' : `Uploading in the ${draftProjectName} has finished`,
            ok_action: () => {},
          });
        }

        setTimeout(() => (this.uploading = false), 1000);
      }
    },

    openLimitPopup(show: boolean = true, bytes: number = 0) {
      this.showLimitPopup = show;
      this.bytesCanBeUsed = bytes;
    },
    setShowUploadLimitBanner(show: boolean) {
      this.showUploadLimitBanner = show;
    },
    setPageLoader(loading: boolean) {
      this.pageLoader = loading;
    },
    changeMobileColumns(show?: boolean) {
      if (typeof show === 'undefined') {
        this.mobileColumns = !this.mobileColumns;
      } else {
        this.mobileColumns = show;
      }
    },
    setCheckedDrafts(ids: number[]) {
      this.checkedDrafts = [...ids];
    },
  },
});

type LoadedType = (length: number, perPage: number) => void;
